import { useState } from "react";
import { Wallet, User, ShoppingCart, Calendar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { DataPagination } from "@/components/shared/DataPagination";
import { useCompanyResgates } from "@/hooks/useResgates";
import type { ResgateAdmin, ResgateTipo } from "@/types/api";

function fmtMoney(v: string) {
  return Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function TipoBadge({ tipo }: { tipo: ResgateTipo }) {
  return tipo === "reais" ? (
    <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50">
      R$
    </Badge>
  ) : (
    <Badge variant="outline" className="text-blue-600 border-blue-300 bg-blue-50">
      Pts
    </Badge>
  );
}

function KpiCard({ label, value, icon: Icon }: { label: string; value: string; icon: React.ElementType }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
          </div>
          <Icon className="h-8 w-8 text-muted-foreground/40" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminResgates() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useCompanyResgates(page);

  const resgates = data?.items ?? [];

  const totalReais = resgates
    .filter((r) => r.tipo === "reais")
    .reduce((acc, r) => acc + Number(r.valor), 0);

  const totalPontos = resgates
    .filter((r) => r.tipo === "pontos")
    .reduce((acc, r) => acc + Number(r.valor), 0);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-foreground">Resgates</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <KpiCard label="Total Resgatado (R$)" value={`R$ ${fmtMoney(String(totalReais))}`} icon={Wallet} />
        <KpiCard label="Total Resgatado (Pts)" value={`${Math.round(totalPontos)} pts`} icon={Wallet} />
        <KpiCard label="Total de Resgates" value={String(data?.total ?? 0)} icon={ShoppingCart} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Histórico de Resgates</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : !resgates.length ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Nenhum resgate registrado ainda.
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground">
                      <th className="text-left pb-3 pr-4 font-medium">
                        <span className="flex items-center gap-1">
                          <User className="h-3.5 w-3.5" />
                          Indicador
                        </span>
                      </th>
                      <th className="text-left pb-3 pr-4 font-medium">
                        <span className="flex items-center gap-1">
                          <ShoppingCart className="h-3.5 w-3.5" />
                          Vendedor
                        </span>
                      </th>
                      <th className="text-right pb-3 pr-4 font-medium">Valor</th>
                      <th className="text-center pb-3 pr-4 font-medium">Tipo</th>
                      <th className="text-right pb-3 font-medium">
                        <span className="flex items-center justify-end gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          Data
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {resgates.map((r: ResgateAdmin) => (
                      <tr key={r.id} className="hover:bg-muted/30 transition-colors">
                        <td className="py-3 pr-4 text-foreground truncate max-w-[180px]">
                          {r.influenciador_email}
                        </td>
                        <td className="py-3 pr-4 text-muted-foreground truncate max-w-[180px]">
                          {r.vendedor_email}
                        </td>
                        <td className="py-3 pr-4 text-right font-semibold">
                          {r.tipo === "reais"
                            ? `R$ ${fmtMoney(r.valor)}`
                            : `${Math.round(Number(r.valor))} pts`}
                        </td>
                        <td className="py-3 pr-4 text-center">
                          <TipoBadge tipo={r.tipo} />
                        </td>
                        <td className="py-3 text-right text-muted-foreground whitespace-nowrap">
                          {fmtDate(r.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4">
                <DataPagination
                  page={data?.page ?? 1}
                  pages={data?.pages ?? 1}
                  onPageChange={setPage}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
